import { useState, useRef, useEffect } from 'react'
import type { ConversationListItem } from '../../api/agent'
import type { Tag } from '../../api/tags'
import ConfirmDialog from '../ConfirmDialog'

interface ConversationListProps {
  conversations: ConversationListItem[]
  selectedId: string | null
  isLoading: boolean
  hasMore: boolean
  onSelect: (id: string) => void
  onNewChat: () => void
  onDelete: (id: string) => void
  onLoadMore: () => void
  filterTags: Tag[]
  onFilterTagsChange: (tags: Tag[]) => void
  availableTags: Tag[]
}

function relativeTime(epochSeconds: number): string {
  const diff = Math.floor(Date.now() / 1000) - epochSeconds
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`
  return new Date(epochSeconds * 1000).toLocaleDateString()
}

export default function ConversationList({
  conversations,
  selectedId,
  isLoading,
  hasMore,
  onSelect,
  onNewChat,
  onDelete,
  onLoadMore,
  filterTags,
  onFilterTagsChange,
  availableTags,
}: ConversationListProps) {
  const [search, setSearch] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<ConversationListItem | null>(null)
  const [showTagFilter, setShowTagFilter] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  // Client-side title filter on top of server-side tag filter
  const filtered = search.trim()
    ? conversations.filter(
        (c) =>
          c.title.toLowerCase().includes(search.toLowerCase()) ||
          c.last_message_preview.toLowerCase().includes(search.toLowerCase())
      )
    : conversations

  // Intersection observer for load-more
  useEffect(() => {
    if (!hasMore || isLoading) return
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) onLoadMore() },
      { threshold: 0.1 }
    )
    const el = bottomRef.current
    if (el) observer.observe(el)
    return () => { if (el) observer.unobserve(el) }
  }, [hasMore, isLoading, onLoadMore])

  function toggleTag(tag: Tag) {
    const exists = filterTags.find((t) => t.id === tag.id)
    onFilterTagsChange(exists ? filterTags.filter((t) => t.id !== tag.id) : [...filterTags, tag])
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-gray-200 space-y-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            data-testid="conv-new-chat"
            onClick={onNewChat}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-primary text-white rounded-lg hover:bg-secondary transition-colors text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Chat
          </button>
          <button
            onClick={() => setShowTagFilter((v) => !v)}
            title="Filter by tags"
            className={`p-2 rounded-lg border transition-colors ${
              filterTags.length > 0
                ? 'bg-primary text-white border-primary'
                : 'border-gray-300 text-gray-500 hover:bg-gray-100'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
            </svg>
          </button>
        </div>

        {/* Tag filter chips */}
        {showTagFilter && availableTags.length > 0 && (
          <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
            {availableTags.map((tag) => {
              const active = filterTags.some((t) => t.id === tag.id)
              return (
                <button
                  key={tag.id}
                  onClick={() => toggleTag(tag)}
                  className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                    active
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {tag.name}
                </button>
              )
            })}
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <svg
            className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            data-testid="conv-search"
            placeholder="Search conversations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && conversations.length === 0 ? (
          <div className="flex items-center justify-center h-24 text-gray-400 text-sm">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin mr-2" />
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-gray-400 text-sm px-4 text-center"
            data-testid="conv-empty-state"
          >
            <svg className="w-8 h-8 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            {search ? 'No matching conversations' : 'No conversations yet'}
          </div>
        ) : (
          <ul data-testid="conv-list">
            {filtered.map((conv) => (
              <li
                key={conv.id}
                data-testid="conv-item"
                className={`group relative border-b border-gray-100 last:border-0 cursor-pointer transition-colors ${
                  selectedId === conv.id
                    ? 'bg-primary/10 border-l-2 border-l-primary'
                    : 'hover:bg-gray-50'
                }`}
                onClick={() => onSelect(conv.id)}
              >
                <div className="px-3 py-3 pr-10">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-gray-900 line-clamp-1 flex-1">
                      {conv.title}
                    </p>
                    <span className="text-xs text-gray-400 flex-shrink-0 mt-0.5">
                      {relativeTime(conv.creation_time)}
                    </span>
                  </div>
                  {conv.last_message_preview && (
                    <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                      {conv.last_message_preview}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    {conv.mode === 'manual' && (
                      <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Manual</span>
                    )}
                    {conv.message_count > 0 && (
                      <span className="text-xs text-gray-400">{conv.message_count} msg{conv.message_count !== 1 ? 's' : ''}</span>
                    )}
                    {conv.tags.slice(0, 2).map((tag) => (
                      <span key={tag.id} className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                        {tag.name}
                      </span>
                    ))}
                    {conv.tags.length > 2 && (
                      <span className="text-xs text-gray-400">+{conv.tags.length - 2}</span>
                    )}
                  </div>
                </div>

                {/* Delete button */}
                <button
                  data-testid="conv-delete-btn"
                  className="absolute right-2 top-3 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all"
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget(conv) }}
                  title="Delete conversation"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Infinite scroll sentinel */}
        <div ref={bottomRef} className="h-2" />
        {isLoading && conversations.length > 0 && (
          <div className="flex justify-center py-3">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Conversation"
        message={`Delete "${deleteTarget?.title}"? This cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={() => { if (deleteTarget) { onDelete(deleteTarget.id); setDeleteTarget(null) } }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}

